import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { of } from 'rxjs/observable/of';
import { catchError } from 'rxjs/operators';
import { ManufacturingOrder } from '../../../../core/models/manufacturing-order';
import { OptionInterface } from '../../../../core/models/option.interface';
import { UserRole } from '../../../../core/models/user-role';
import { UserRoleApiService } from '../../../../core/services/api/user-role-api.service';
import { find } from '../../../../core/utils/collection.utils';
import { buildOptions } from '../../../../core/utils/option.utils';
import { formChanged } from '../../../../core/validators/form-changed.validator';
import { AbstractEditionModalForm } from '../edition-modal/abstract-edition-modal-form';
import { PieceQuantityValidation } from './piece-quantity-validator';

@Component({
  selector: 'app-manufacturing-order-pieces-change-form',
  templateUrl: './manufacturing-order-pieces-change-form.component.html',
  styleUrls: ['./manufacturing-order-pieces-change-form.component.css'],
  providers: [
    {
      provide: AbstractEditionModalForm,
      useExisting: forwardRef(
        () => ManufacturingOrderPiecesChangeFormComponent
      ),
    },
  ],
})
export class ManufacturingOrderPiecesChangeFormComponent extends AbstractEditionModalForm<any> implements OnInit, OnChanges {
  form: AbstractControl;
  receiverOptions: OptionInterface[];
  selectedReciver: { hash: string, label: string, isRole: boolean, isUser: boolean }[] = [];
  @Input() usersRoles: UserRole[];
  @Input() manufacturingOrder: ManufacturingOrder;
  /**
   * When true, the user no longer picks recipients: the modal resolves them from
   * `recipientNames` using the existing /users-roles/search endpoint and shows them
   * read-only. Used by the BY_PART scheduler inline edit; other usages keep the
   * original selector by leaving this off.
   */
  @Input() useRecipientsMatrix = false;
  @Input() recipientNames: string[] = [];
  @Output() searchReceiver = new EventEmitter<string>();

  constructor(
    private fb: FormBuilder,
    private pieceQuantityValidation: PieceQuantityValidation,
    private userRoleApiService: UserRoleApiService
  ) {
    super();
  }

  ngOnInit() {
    this.initForm();
    if (this.useRecipientsMatrix) {
      this.resolveMatrixRecipients();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('usersRoles')) {
      this.buildUsersRolesOptions();
    }
    if (changes.hasOwnProperty('manufacturingOrder') && this.form) {
      this.updateForm();
    }
    if ((changes.hasOwnProperty('recipientNames') || changes.hasOwnProperty('useRecipientsMatrix'))
        && this.useRecipientsMatrix && this.form) {
      this.resolveMatrixRecipients();
    }
  }

  buildUsersRolesOptions() {
    if (this.selectedReciver && this.usersRoles) {
      const userRolesFiltered = this.usersRoles.filter(userRole => !this.selectedReciver.some(item => item.hash === userRole.hash));
      this.receiverOptions = buildOptions(userRolesFiltered, 'hash', 'name');
    }
  }

  onSelect(event) {
    if (!this.selectedReciver.some(item => item.hash === event.value)) {
      const userRole: UserRole = find(
        (ur: UserRole) => ur.hash === event.value,
        this.usersRoles
      );
      this.selectedReciver.push({
        hash: event.value,
        label: event.label,
        isRole: userRole ? userRole.isRole : null,
        isUser: userRole ? userRole.isUser : null
      });
      this.buildUsersRolesOptions();
    }
    this.syncHasReciverFlag();
    this.form.get('receiverHash').setValue('');
  }

  removeItem(item: any) {
    const index = this.selectedReciver.indexOf(item);
    if (index !== -1) {
      this.selectedReciver.splice(index, 1);
      this.buildUsersRolesOptions();
    }
    this.syncHasReciverFlag();
  }

  /**
   * Resolves each configured name via /users-roles/search and pushes the exact
   * match into selectedReciver. The backend search runs
   * `firstName LIKE %q% OR lastName LIKE %q%`, so passing the whole
   * "Firstname Lastname" string never matches; we send only the first token
   * and then filter results client-side by full-name equality (case-insensitive).
   * Unmatched names are silently skipped — the modal still works with whoever
   * could be resolved.
   */
  private resolveMatrixRecipients() {
    this.selectedReciver = [];
    if (!this.recipientNames || this.recipientNames.length === 0) {
      this.syncHasReciverFlag();
      return;
    }
    const searches = this.recipientNames.map(name => {
      const firstToken = (name || '').trim().split(/\s+/)[0];
      if (!firstToken) {
        return of([] as UserRole[]);
      }
      return this.userRoleApiService.searchUsersAndRoles(firstToken).pipe(catchError(() => of([])));
    });
    forkJoin(searches).subscribe(results => {
      this.recipientNames.forEach((name, idx) => {
        const matches = (results[idx] as UserRole[]) || [];
        const exact = matches.find(ur => ur.isUser && this.namesMatch(ur.name, name));
        if (exact && !this.selectedReciver.some(item => item.hash === exact.hash)) {
          this.selectedReciver.push({
            hash: exact.hash,
            label: exact.name,
            isRole: false,
            isUser: true,
          });
        }
      });
      this.syncHasReciverFlag();
    });
  }

  private namesMatch(a: string, b: string): boolean {
    return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
  }

  private syncHasReciverFlag() {
    if (!this.form) {
      return;
    }
    if (this.selectedReciver.length > 0) {
      this.form.get('hasReciver').setValue(true);
    } else {
      this.form.get('hasReciver').setValue(null);
    }
    // The form-level matrix validator reads selectedReciver, so we re-evaluate
    // it whenever the resolved set changes (otherwise save stays disabled
    // even after recipients arrive).
    this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
  }

  initForm() {
    const currentQuantity = this.manufacturingOrder && this.manufacturingOrder.quantity
      ? this.manufacturingOrder.quantity
      : null;
    this.form = this.fb.group({
      hasReciver: [null],
      receiverHash: [''],
      pieceQuantity: [
        '',
        [
          Validators.required,
          Validators.max(9999999999999999999),
          this.pieceQuantityValidation.pieceQuantityValidation(currentQuantity),
        ],
      ],
      realPieceQuantity: [''],
    });
    this.updateForm();
  }

  updateForm(manufacturingOrderComment?: any, _params?: any) {
    if (!this.form) {
      return;
    }
    // In matrix mode the recipients are reloaded by resolveMatrixRecipients;
    // in legacy mode we wipe them so the previous edit doesn't leak across opens.
    if (!this.useRecipientsMatrix) {
      this.selectedReciver = [];
    }
    this.buildUsersRolesOptions();
    const currentQuantity = this.manufacturingOrder
      ? this.manufacturingOrder.quantity
      : null;
    const value = {
      receiverHash: manufacturingOrderComment
        ? manufacturingOrderComment.receiverHash
        : '',
      pieceQuantity: manufacturingOrderComment
        ? manufacturingOrderComment.pieceQuantity
        : currentQuantity,
      realPieceQuantity: manufacturingOrderComment
        ? manufacturingOrderComment.pieceQuantity
        : currentQuantity,
    };
    this.form.get('pieceQuantity').setValidators([
      Validators.required,
      Validators.max(9999999999999999999),
      this.pieceQuantityValidation.pieceQuantityValidation(currentQuantity),
    ]);
    this.resetForm(value);
  }

  resetForm(value) {
    // In matrix mode the receivers are resolved asynchronously; until they're
    // populated we keep the form invalid so the save button stays disabled
    // (prevents firing the POST with receivers: [] which the backend rejects).
    const validators: any[] = [formChanged(value)];
    if (this.useRecipientsMatrix) {
      validators.push(() =>
        this.selectedReciver.length === 0 ? { recipientsResolving: true } : null
      );
    }
    this.form.setValidators(validators);
    this.form.reset(value);
  }

  getFormData() {
    const formValue = this.form.value;

    return {
      receivers: this.selectedReciver.map(reciver => {
        return {
          hash: reciver.hash,
          isRole: reciver.isRole,
          isUser: reciver.isUser
        };
      }),
      pieceQuantity: formValue.pieceQuantity,
    };
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/shared/components/modals/manufacturing-order-pieces-change-form/manufacturing-order-pieces-change-form.component.ts